import { Filter } from '@fabric-ui/components';
import { AST, Binary, ColumnRefItem, Parser as NodeSqlParser } from 'node-sql-parser';
import { mergeAnd, mergeOr } from '../merge-filter';
import { conditionBuilderFactory, functionConditionBuilder, makeNullCondition } from './condition-builder';
import { SqlParserError } from './SqlParserError';
import {
  getColumnName,
  getFunctionArguments,
  getFunctionName,
  isAndOperator,
  isAndOrOperator,
  isBinaryExpr,
  isColumnRefItem,
  isFunctionNode,
  isNullOperator,
  isOperator,
  isSupportedFunction,
  isValueExpr,
  isValueExprList,
  type ASTNode,
  type Operators,
  type Values,
} from './SqlToFilterConverter.utils';
import { parseFunctionArguments, parseValue } from './value-parser';

type ConditionData =
  | {
      type: 'condition';
      field: string;
      col: SqlToFilterConverterColumnType;
      op: Operators;
      value: Values | Values[];
    }
  | {
      type: 'function';
      field: string;
      col: SqlToFilterConverterColumnType;
      op: Operators;
      functionName: string;
      functionArgs: Values[];
    };

export type SqlToFilterConverterColumnType = 'string' | 'number' | 'boolean' | 'date';
export type SqlToFilterConverterColumnSchema = Record<string, SqlToFilterConverterColumnType>;

export type SqlToFilterConverterOptions = {
  enableStrictColumnTypeCheck?: boolean;
  preserveGroups?: boolean;
};

const sqlToFilterConverter = (columnSchema: SqlToFilterConverterColumnSchema, options: SqlToFilterConverterOptions = {}) => {
  const parser = new NodeSqlParser();

  const stringToAST = (sql: string) => {
    try {
      const parsed = parser.astify(sql);
      const ast = Array.isArray(parsed) ? parsed[0] : parsed;
      return ast;
    } catch (error) {
      throw new SqlParserError('Invalid SQL', { cause: error });
    }
  };

  const prepareConditionData = (left: ColumnRefItem, op: Operators, right: ASTNode): ConditionData => {
    const field = getColumnName(left);
    const col = getColumnType(left);

    if (isValueExpr(right) || isValueExprList(right)) {
      const values = parseValue(right);
      return { field, col, op, value: values, type: 'condition' } as const;
    }

    if (isFunctionNode(right)) {
      const functionName = getFunctionName(right);
      if (isSupportedFunction(functionName)) {
        const args = getFunctionArguments(right);
        const parsedArguments = parseFunctionArguments(
          functionName,
          args.map(x => x.value)
        );
        return { field, col, op, functionArgs: parsedArguments, functionName, type: 'function' } as const;
      }
    }

    throw new SqlParserError(`Invalid condition for column '${col}' with operator '${op}'.`, { node: left });
  };

  const processBinaryExprNode = (node: Binary): Filter => {
    if (isAndOrOperator(node.operator)) {
      return buildComplexExpression(node);
    }

    if (isColumnRefItem(node.left) && isOperator(node.operator)) {
      const conditionDate = prepareConditionData(node.left, node.operator, node.right);
      return createCondition(conditionDate);
    }

    throw new SqlParserError('Unsupported binary expression node', { node });
  };

  const processNode = (node: ASTNode): Filter => {
    if (isBinaryExpr(node)) {
      return processBinaryExprNode(node);
    }

    throw new SqlParserError(`Unsupported node type: "${node?.type}"`, { node });
  };

  const createCondition = (data: ConditionData) => {
    const handleFunctionCondition = (fieldName: string, functionName: string, args: Values[]) => {
      if (isSupportedFunction(functionName)) {
        return functionConditionBuilder(fieldName, functionName, args);
      }
      throw new SqlParserError(`Unsupported function '${functionName}'`);
    };

    const handleCondition = (field: string, col: SqlToFilterConverterColumnType, op: Operators, value: Values | Values[]) => {
      if (isNullOperator(op)) {
        if (value !== null) {
          throw new SqlParserError(`NULL operators cannot have values. received: ${value}`);
        }
        return makeNullCondition(field, col, op);
      }

      if ((op === 'IN' || op === 'NOT IN') && Array.isArray(value) && value.some(v => v === null)) {
        throw new SqlParserError(`NULL is not supported inside IN/NOT IN. Use IS NULL instead.`);
      }

      const conditionBuilder = conditionBuilderFactory(col);

      // @ts-expect-error: fix type @nizans
      return conditionBuilder(field, op, value);
    };

    switch (data.type) {
      case 'function': {
        const { field, functionArgs, functionName } = data;
        return handleFunctionCondition(field, functionName, functionArgs);
      }
      case 'condition': {
        const { field, col, op, value } = data;
        return handleCondition(field, col, op, value);
      }
      default:
    }
    data satisfies never;
    throw new SqlParserError(`Unsupported condition data type`);
  };

  const buildComplexExpression = (node: ASTNode, enclosingOp: 'AND' | 'OR' | null = null) => {
    const operatorStack: ('AND' | 'OR')[] = [];
    const filterStack: Filter[] = [];

    const buildFilter = (op: 'AND' | 'OR', left: Filter, right: Filter): Filter =>
      isAndOperator(op) ? mergeAnd(left, right) : mergeOr(left, right);

    const wrapGroup = (op: 'AND' | 'OR', left: Filter, right: Filter): Filter => {
      const key = isAndOperator(op) ? 'and' : 'or';
      const inner = buildFilter(op, left, right);
      return { [key]: { operands: [inner] } };
    };

    const collapse = (incoming: 'AND' | 'OR' | null) => {
      while (operatorStack.length && precedence(operatorStack[operatorStack.length - 1]) >= precedence(incoming)) {
        const currentOperator = operatorStack.pop()!;
        const rightOperand = filterStack.pop()!;
        const leftOperand = filterStack.pop()!;
        const filter = buildFilter(currentOperator, leftOperand, rightOperand);
        filterStack.push(filter);
      }
    };

    const traverse = (n: ASTNode, currentOp: 'AND' | 'OR' | null = null) => {
      if (isBinaryExpr(n) && isAndOrOperator(n.operator) && !n.parentheses) {
        traverse(n.left, n.operator);
        const op = n.operator;
        collapse(op);
        operatorStack.push(op);
        traverse(n.right, n.operator);
        return;
      }

      if (isBinaryExpr(n) && isAndOrOperator(n.operator)) {
        const left = buildComplexExpression(n.left, n.operator);
        const right = buildComplexExpression(n.right, n.operator);
        const isSameOp = options.preserveGroups && currentOp === n.operator;
        const builder = isSameOp ? wrapGroup(n.operator, left, right) : buildFilter(n.operator, left, right);
        filterStack.push(builder);
        return;
      }

      filterStack.push(processNode(n));
    };

    traverse(node, enclosingOp);
    collapse(null);
    return filterStack[0];
  };

  const getColumnType = (node: ColumnRefItem): SqlToFilterConverterColumnType => {
    const columnName = getColumnName(node);

    if (columnSchema[columnName]) {
      return columnSchema[columnName];
    }
    if (node.table && columnSchema[node.table]) {
      return columnSchema[node.table];
    }

    if (options.enableStrictColumnTypeCheck) {
      throw new SqlParserError(`Could not find columnType for column: ${columnName}`);
    }

    return 'string';
  };

  const parseAst = (ast: AST): Filter => {
    if (ast.type !== 'select') {
      throw new SqlParserError('Invalid SQL: Expected a SELECT statement');
    }

    if (!ast.where) {
      return {};
    }

    const root = processNode(ast.where);

    if (options.preserveGroups && isBinaryExpr(ast.where) && isAndOrOperator(ast.where.operator) && ast.where.parentheses) {
      const key = isAndOperator(ast.where.operator) ? 'and' : 'or';
      return { [key]: { operands: [root] } };
    }

    return root;
  };

  const parse = (sql: string): Filter => {
    // Used in catch block
    // eslint-disable-next-line custom-rules/no-let
    let ast: AST | undefined;
    try {
      ast = stringToAST(sql);
      return parseAst(ast);
    } catch (error) {
      if (error instanceof SqlParserError) {
        error.context = { ...error.context, sql, ast };
        throw error;
      }
      throw new SqlParserError('Unexpected error', { sql, cause: error, ast });
    }
  };

  return {
    parse,
  };
};

const precedence = (op: 'AND' | 'OR' | null): number => precedenceMap.get(op) || 0;
const precedenceMap = new Map([
  ['AND', 2],
  ['OR', 1],
  [null, 0],
]);

export const SqlToFilterConverter = {
  create: (columnSchema: SqlToFilterConverterColumnSchema, options?: SqlToFilterConverterOptions) =>
    sqlToFilterConverter(columnSchema, options),
};
